import React, { useState } from 'react'
import { 
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, isToday, isSameMonth, addWeeks, subWeeks,
  addMonths, subMonths, isSameDay, parseISO
} from 'date-fns'
import { ChevronLeft, ChevronRight, Calendar, List, CalendarDays } from 'lucide-react'

type ViewMode = 'day' | 'week' | 'month' | 'agenda'

interface CalendarEvent {
  date: string
  title: string
  type: string
  description?: string
  color?: string
  onClick?: () => void
}

interface CalendarWithViewsProps {
  events: CalendarEvent[]
  onDateSelect?: (date: Date) => void
  renderDayContent?: (date: Date, events: CalendarEvent[]) => React.ReactNode
}

export function CalendarWithViews({ events, onDateSelect, renderDayContent }: CalendarWithViewsProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [currentDate, setCurrentDate] = useState(new Date())

  const navigatePrevious = () => {
    if (viewMode === 'day') {
      const newDate = new Date(currentDate)
      newDate.setDate(newDate.getDate() - 1)
      setCurrentDate(newDate)
    } else if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1))
    } else if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1))
    }
  }

  const navigateNext = () => {
    if (viewMode === 'day') {
      const newDate = new Date(currentDate)
      newDate.setDate(newDate.getDate() + 1)
      setCurrentDate(newDate)
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1))
    } else if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1))
    }
  }

  const navigateToday = () => {
    setCurrentDate(new Date())
  }

  const getDateRange = () => {
    if (viewMode === 'day') {
      return {
        start: currentDate,
        end: currentDate
      }
    } else if (viewMode === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 0 }),
        end: endOfWeek(currentDate, { weekStartsOn: 0 })
      }
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      }
    }
  }

  const getDaysToDisplay = () => {
    const { start, end } = getDateRange()
    
    if (viewMode === 'day') {
      return [currentDate]
    } else if (viewMode === 'month') {
      // Include days from previous/next month to fill the calendar grid
      const startWeek = startOfWeek(start, { weekStartsOn: 0 })
      const endWeek = endOfWeek(end, { weekStartsOn: 0 })
      return eachDayOfInterval({ start: startWeek, end: endWeek })
    } else {
      return eachDayOfInterval({ start, end })
    }
  }

  const getEventsForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return events.filter(event => event.date === dateStr)
  }

  const getHeaderText = () => {
    if (viewMode === 'day') {
      return format(currentDate, 'EEEE, MMMM d, yyyy')
    } else if (viewMode === 'week') {
      const { start, end } = getDateRange()
      if (format(start, 'MMM yyyy') === format(end, 'MMM yyyy')) {
        return `${format(start, 'MMM d')} - ${format(end, 'd, yyyy')}`
      } else {
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
      }
    } else if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy')
    } else {
      return 'Upcoming Events'
    }
  }

  const sortedEvents = [...events]
    .filter(event => {
      // Skip events without dates
      if (!event.date) return false
      try {
        const eventDate = parseISO(event.date)
        return eventDate >= new Date()
      } catch (error) {
        console.warn('Invalid date in event:', event)
        return false
      }
    })
    .sort((a, b) => {
      if (!a.date || !b.date) return 0
      return a.date.localeCompare(b.date)
    })

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{getHeaderText()}</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={navigateToday}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Today
            </button>
            <button
              onClick={navigatePrevious}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={navigateNext}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* View Mode Selector */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('day')}
            className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
              viewMode === 'day'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            <span>Day</span>
          </button>
          <button
            onClick={() => setViewMode('week')}
            className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
              viewMode === 'week'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            <span>Week</span>
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
              viewMode === 'month'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Calendar className="w-4 h-4" />
            <span>Month</span>
          </button>
          <button
            onClick={() => setViewMode('agenda')}
            className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
              viewMode === 'agenda'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <List className="w-4 h-4" />
            <span>Agenda</span>
          </button>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="p-4">
        {viewMode === 'agenda' ? (
          /* Agenda View */
          <div className="space-y-3">
            {sortedEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No upcoming events
              </div>
            ) : (
              sortedEvents.slice(0, 20).map((event, index) => {
                const eventDate = parseISO(event.date)
                const isEventToday = isToday(eventDate)
                
                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      isEventToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${
                            isEventToday ? 'text-blue-600' : 'text-gray-600'
                          }`}>
                            {format(eventDate, 'EEE, MMM d')}
                          </span>
                          {isEventToday && (
                            <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                              Today
                            </span>
                          )}
                        </div>
                        <div className="mt-1">
                          <div className="font-medium text-gray-900">{event.title}</div>
                          {event.description && (
                            <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                          )}
                        </div>
                      </div>
                      {event.type && (
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          event.color || 'bg-gray-100 text-gray-700'
                        }`}>
                          {event.type}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        ) : viewMode === 'day' ? (
          /* Day View */
          <div className="space-y-4">
            {getDaysToDisplay().map(date => {
              const dayEvents = getEventsForDay(date)
              
              return (
                <div key={date.toISOString()} className="border rounded-lg p-4">
                  <div className="space-y-3">
                    {dayEvents.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No events for this day
                      </div>
                    ) : (
                      dayEvents.map((event, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            event.color || 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="font-medium text-gray-900">{event.title}</div>
                          {event.description && (
                            <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                          )}
                          {event.type && (
                            <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                              event.color || 'bg-gray-100 text-gray-700'
                            }`}>
                              {event.type}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          /* Week/Month Grid View */
          <div>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-xs font-medium text-gray-600 text-center py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {getDaysToDisplay().map(date => {
                const dayEvents = getEventsForDay(date)
                const isCurrentMonth = viewMode === 'week' || isSameMonth(date, currentDate)
                const isSelectedDay = isSameDay(date, currentDate)
                
                return (
                  <div
                    key={date.toISOString()}
                    onClick={() => onDateSelect?.(date)}
                    className={`
                      min-h-[60px] p-1 border rounded-lg cursor-pointer transition-colors
                      ${!isCurrentMonth ? 'bg-gray-50 opacity-50' : 'bg-white'}
                      ${isToday(date) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}
                      ${isSelectedDay ? 'ring-2 ring-blue-400' : ''}
                      hover:bg-gray-50
                    `}
                  >
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      {format(date, 'd')}
                    </div>
                    
                    {renderDayContent ? (
                      renderDayContent(date, dayEvents)
                    ) : (
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, viewMode === 'week' ? 3 : 2).map((event, idx) => (
                          <div
                            key={idx}
                            className={`text-xs px-1 py-0.5 rounded truncate ${
                              event.color || 'bg-gray-100 text-gray-700'
                            }`}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > (viewMode === 'week' ? 3 : 2) && (
                          <div className="text-xs text-gray-500 text-center">
                            +{dayEvents.length - (viewMode === 'week' ? 3 : 2)} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}